/**
 * A alçada da margem (`TP-064`): `GET /casos/{id}/alcada`, a sugestão (`POST …/alcada/sugestao`) e o registro
 * (`POST …/alcada`). A margem vai no CORPO, nunca na URL: é dado de alçada, e a URL acaba em log, proxy e histórico.
 */

import { queryOptions } from '@tanstack/react-query';
import type { Schemas } from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import { AO_VIVO } from '../api/casos';
import {
  ehNivelDeAlcada,
  type AlcadaDoCaso,
  type NivelDeAlcada,
  type ReguaDeAlcada,
  type RegistroDeAlcada,
  type SituacaoDaSugestao,
  type SugestaoDeAlcada,
} from './tipos';

type FioDaAlcada = Schemas['AlcadaDoCasoResponse'];
type FioDaSugestao = Schemas['SugestaoDeAlcadaResponse'];
type FioDoRegistro = Schemas['RegistroDeAlcadaResponse'];
type FioDaRegua = Schemas['ReguaDeAlcadaResponse'];

export const chavesDeAlcada = {
  doCaso: (casoId: string) => ['casos', casoId, 'alcada'] as const,
};

function nivelOuNulo(valor: string | null | undefined): NivelDeAlcada | null {
  return ehNivelDeAlcada(valor) ? valor : null;
}

function mapearRegua(f: FioDaRegua | null | undefined): ReguaDeAlcada | null {
  return f ? { fonte: f.fonte ?? '', versao: f.versao } : null;
}

function mapearRegistro(f: FioDoRegistro): RegistroDeAlcada {
  return {
    id: f.id,
    margem: f.margem,
    faixa: f.faixa ?? null,
    sugerida: nivelOuNulo(f.sugerida),
    // O CHECK do banco garante o vocabulário; `gerencia` só cobre um token novo que a tela ainda não conhece.
    aprovadora: nivelOuNulo(f.aprovadora) ?? 'gerencia',
    motivo: f.motivo ?? null,
    regua: mapearRegua(f.regua),
    registradoEm: new Date(f.registradoEm),
    registradoPorNome: f.registradoPorNome ?? null,
  };
}

export function mapearAlcada(fio: FioDaAlcada): AlcadaDoCaso {
  return {
    podeRegistrar: fio.podeRegistrar,
    vigente: fio.vigente ? mapearRegistro(fio.vigente) : null,
    historico: (fio.historico ?? []).map(mapearRegistro),
  };
}

export function mapearSugestao(fio: FioDaSugestao): SugestaoDeAlcada {
  return {
    situacao: (fio.situacao ?? 'sem_regua') as SituacaoDaSugestao,
    faixa: fio.faixa ?? null,
    sugerida: nivelOuNulo(fio.sugerida),
    regua: mapearRegua(fio.regua),
    mensagem: fio.mensagem ?? null,
    correlationId: fio.correlationId,
  };
}

export function alcadaDoCaso(casoId: string) {
  return queryOptions({
    queryKey: chavesDeAlcada.doCaso(casoId),
    queryFn: ({ signal }) =>
      requisitar<FioDaAlcada>(`/casos/${encodeURIComponent(casoId)}/alcada`, { sinal: signal }).then(mapearAlcada),
    ...AO_VIVO,
  });
}

/** Não grava nada: só diz o que a régua sugere para a margem. */
export function sugerirAlcada(casoId: string, margem: number, sinal?: AbortSignal): Promise<SugestaoDeAlcada> {
  return requisitar<FioDaSugestao>(`/casos/${encodeURIComponent(casoId)}/alcada/sugestao`, {
    method: 'POST',
    corpo: { margem },
    sinal,
  }).then(mapearSugestao);
}

/**
 * Registra quem aprovou. O ator NÃO vai no corpo (o back lê da sessão), nem a sugestão (o back recalcula). Motivo
 * faltando volta `400 ALCADA_REASON_REQUIRED`; Caso fora de `orcamento_emitido`, `409 ALCADA_OUTSIDE_QUOTE`.
 */
export function registrarAlcada(
  casoId: string,
  registro: { margem: number; aprovadora: NivelDeAlcada; motivo: string | null },
): Promise<AlcadaDoCaso> {
  return requisitar<FioDaAlcada>(`/casos/${encodeURIComponent(casoId)}/alcada`, {
    method: 'POST',
    corpo: registro,
  }).then(mapearAlcada);
}
